import React, { useState, useEffect, useContext } from 'react';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Typography,
  TablePagination,
  TextField,
  InputAdornment,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import AuthContext from '../../contexts/AuthContext';
import InviteUserDialog from './InviteUserDialog';

/**
 * Displays a list of pending invitations and triggers new invites.
 * Props:
 *  - organizationId: UUID of the organization
 */
export default function InvitationsTabContent({ organizationId }) {
  const apiBase = process.env.REACT_APP_PORTAL_API_URL || '';
  const { tokens } = useContext(AuthContext);
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [searchInput, setSearchInput] = useState('');
  const [searchText, setSearchText] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');

  const loadInvitations = () => {
    if (!tokens?.id_token || !organizationId) return;
    setLoading(true);
    const params = new URLSearchParams();
    params.append('organization_id', organizationId);
    if (searchText) params.append('search_text', searchText);
    if (selectedStatus) params.append('status', selectedStatus);
    params.append('skip', page * rowsPerPage);
    params.append('limit', rowsPerPage);
    fetch(`${apiBase}/invitations?${params.toString()}`, {
      headers: { Authorization: `Bearer ${tokens.id_token}` },
    })
      .then(res => {
        if (!res.ok) throw new Error('Failed to load invitations');
        return res.json();
      })
      .then(data => { 
        setInvitations(data.items || []); 
        setTotal(data.total || 0);
        setError(null); 
      })
      .catch(err => setError(err.message || 'Error loading invitations'))
      .finally(() => setLoading(false));
  };

  useEffect(loadInvitations, [tokens, organizationId, page, rowsPerPage, searchText, selectedStatus]);

  const handleChangePage = (_, newPage) => {
    setPage(newPage);
  };
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Debounce search input similar to Organizations page
  useEffect(() => {
    const id = setTimeout(() => {
      setSearchText(searchInput);
      setPage(0);
    }, 300);
    return () => clearTimeout(id);
  }, [searchInput]);
  const handleStatusChange = (e) => {
    setSelectedStatus(e.target.value);
    setPage(0);
  };

  return (
    <>
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <TextField
          size="small"
          label="Search Invitations"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          placeholder="Search by email"
          sx={{ width: 300 }}
          InputProps={{
            endAdornment: searchInput && (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearchInput('')}>
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel id="status-filter-label">Status</InputLabel>
            <Select
              labelId="status-filter-label"
              value={selectedStatus}
              label="Status"
              onChange={handleStatusChange}
            >
              <MenuItem value="">All Statuses</MenuItem>
              <MenuItem value="Pending">Pending</MenuItem>
              <MenuItem value="Accepted">Accepted</MenuItem>
              <MenuItem value="Declined">Declined</MenuItem>
            </Select>
          </FormControl>
          <Button variant="contained" onClick={() => setDialogOpen(true)}>
            Invite User
          </Button>
        </Box>
      </Box>
      <Paper>
        <TableContainer>
          {error ? (
            <Box display="flex" justifyContent="center" alignItems="center" p={2}>
              <Typography color="error">{error}</Typography>
            </Box>
          ) : loading ? (
            <Box display="flex" justifyContent="center" alignItems="center" p={2}>
              <CircularProgress />
            </Box>
          ) : (
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Email</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Created at</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {invitations.map(inv => (
                  <TableRow key={inv.id} hover>
                    <TableCell>{inv.email}</TableCell>
                    <TableCell>{inv.role}</TableCell>
                    <TableCell>{inv.status}</TableCell>
                    <TableCell>{new Date(inv.created_at).toLocaleDateString()}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </TableContainer>
      </Paper>
      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={total}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
      <InviteUserDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        organizationId={organizationId}
        onInvited={loadInvitations}
      />
    </>
  );
}
